import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  basisPointsToPercent,
  percentToBasisPoints,
} from 'features/deals/DealStages/EventActionCards/utils';
import { CashFlowModelingInputs } from 'features/drilldown/cashflows/CashFlowModelingInputs';
import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';
import { ChartSkeletonLoader } from 'features/pages/market-data/components/ChartSkeleton';
import { URLMapDeprecated } from 'features/pages/market-data/shared';
import BidSummary from 'features/pages/marketplace/BiddingAndPricing/BidSummary/BidSummary';
import MaturityData from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/MaturityData';
import { YieldCurveData } from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/types';
import {
  SOFR_YIELD_CURVE_FILE,
  TREASURY_YIELD_CURVE_FILE,
  lookupAtPricePercent,
  normalizePriceYieldMatrix,
  normalizeYieldCurveData,
  rateAsBps,
  rateAsPercent,
} from 'features/pages/marketplace/BiddingAndPricing/YieldMatrix/utils';
import YieldToPriceChart from 'features/pages/marketplace/BiddingAndPricing/YieldToPriceChart';

import { useQuery } from '@apollo/client';

import { RateType } from '__generated__/globalTypes';
import { GetMarketData } from 'query/__generated__/getMarketData';
import { GET_MARKET_DATA } from 'query/getMarketData';

import { GetShortCarves_deal_carves_carve_summary } from 'query/getCarves';
interface YieldMatrixBidDetailsProps {
  onPriceSelect: (value: string) => void;
  formValues: CashFlowsValidValues;
  onFormChange: (values: CashFlowsValidValues) => void;
  carveSummaryData: GetShortCarves_deal_carves_carve_summary | null;
  loading: boolean;
  buyerPrice: number;
}

const YieldMatrixBidDetails: React.FC<YieldMatrixBidDetailsProps> = ({
  onPriceSelect,
  formValues,
  onFormChange,
  carveSummaryData,
  loading,
  buyerPrice,
}) => {
  const [chartWidth, setChartWidth] = useState(800);
  const chartParentRef = useRef<HTMLDivElement>(null);
  const [bidBasisPoints, setBidBasisPoints] = useState(buyerPrice || 0);
  const [ycChartData, setYcChartData] = useState<YieldCurveData | null>(null);
  const [ycChartDataMap, setYcChartDataMap] = useState<URLMapDeprecated>({});
  const ycChartDataUrl =
    formValues.rateType === RateType.RiskFree ? TREASURY_YIELD_CURVE_FILE : SOFR_YIELD_CURVE_FILE;
  const [spreadDuration, setSpreadDuration] = useState<number | undefined>(undefined);

  const [spread, setSpread] = useState<number | undefined>(undefined);
  const [yieldDuration, setYieldDuratio] = useState<number | undefined>(undefined);

  const [netYield, setNetYield] = useState<number | undefined>(undefined);

  const handleFormChange = (newValues: Partial<CashFlowsValidValues>) => {
    onFormChange({ ...formValues, ...newValues });
  };

  const {
    data: marketData,
    loading: marketDataLoading,
    error: marketDataError,
  } = useQuery<GetMarketData>(GET_MARKET_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.marketData) {
        const urlMap: URLMapDeprecated = {};
        Object.entries(data.marketData.urlMap).forEach(([key, value]) => {
          urlMap[key] = String(value);
        });
        setYcChartDataMap(urlMap);
      }
    },
  });

  const unpaidBalance = carveSummaryData?.unpaidPrincipalBalance || 0;

  // Normalize dollar prices onto price-percent space once, using the carve
  // balance (cents). The bid and the chart share that space.
  const yieldPoints = useMemo(
    () =>
      normalizePriceYieldMatrix(
        carveSummaryData?.cashFlows?.price_yield_matrix?.yield_matrix,
        unpaidBalance,
      ),
    [carveSummaryData, unpaidBalance],
  );
  const spreadPoints = useMemo(
    () =>
      normalizePriceYieldMatrix(
        carveSummaryData?.cashFlows?.price_yield_matrix?.spread_matrix,
        unpaidBalance,
      ),
    [carveSummaryData, unpaidBalance],
  );

  useEffect(() => {
    // The bid is already a price-percent — the space the matrix is now in.
    const bidPercent = parseFloat(basisPointsToPercent(bidBasisPoints));

    // lookupAtPricePercent returns null off the curve / on an empty series, so
    // a carve refetch that empties the matrix or moves its bounds clears the
    // value instead of leaving the previous carve's stale one (cf. ENG-2862).
    const yieldResult = lookupAtPricePercent(yieldPoints, bidPercent);
    setNetYield(yieldResult ? rateAsPercent(yieldResult.rate) : undefined);
    setYieldDuratio(yieldResult?.durationYears);

    const spreadResult = lookupAtPricePercent(spreadPoints, bidPercent);
    setSpread(spreadResult ? rateAsBps(spreadResult.rate) : undefined);
    setSpreadDuration(spreadResult?.durationYears);
  }, [bidBasisPoints, yieldPoints, spreadPoints]);

  useEffect(() => {
    if (ycChartDataUrl && ycChartDataMap[ycChartDataUrl]) {
      fetch(ycChartDataMap[ycChartDataUrl])
        .then((response) => response.json())
        .then((data: YieldCurveData) => {
          setYcChartData(normalizeYieldCurveData(data, ycChartDataUrl));
        });
    }
  }, [ycChartDataUrl, ycChartDataMap]);

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartParentRef.current) {
        const parentWidth = chartParentRef.current.offsetWidth;
        setChartWidth(parentWidth - 310);
      }
    };

    updateChartWidth();
    window.addEventListener('resize', updateChartWidth);
    return () => window.removeEventListener('resize', updateChartWidth);
  }, []);

  const bidPx = basisPointsToPercent(bidBasisPoints);

  const handlePointClick = (pricePercent: number) => {
    const price = parseFloat(pricePercent.toFixed(3));
    const basisPoints = percentToBasisPoints(price.toString());

    setBidBasisPoints(basisPoints);
    // Convert basis points back to the format expected by the bid card input.
    // The bid-basis-points change recomputes yield/spread/duration from the
    // matrix in the effect above, so we no longer set them from the click
    // payload (which previously put the yield value into the duration slot).
    onPriceSelect(basisPointsToPercent(basisPoints));
  };

  return (
    <div>
      <div className="flex gap-[40px] pb-[15px]">
        <CashFlowModelingInputs
          formValues={formValues}
          onSubmit={handleFormChange}
          withServicingRate
        />
      </div>
      <MaturityData
        ycChartData={ycChartData}
        formValues={formValues}
        onCurveSelect={handleFormChange}
      />

      <div className="flex flex-col gap-8 md:flex-row" ref={chartParentRef}>
        <div style={{ width: `${chartWidth}px` }} className="w-full">
          {loading ? (
            <ChartSkeletonLoader height={500} width={chartWidth - 25} />
          ) : (
            <YieldToPriceChart
              yieldPoints={yieldPoints}
              spreadPoints={spreadPoints}
              bidPx={bidPx}
              width={chartWidth - 25}
              onPointClick={handlePointClick}
            />
          )}
        </div>

        <div className="min-w-[300px] pr-4">
          <BidSummary
            netCoupon={carveSummaryData?.netCoupon || 0}
            unpaidBalance={carveSummaryData?.unpaidPrincipalBalance || 0}
            netYield={netYield}
            spread={spread}
            yieldDuration={yieldDuration}
            spreadDuration={spreadDuration}
            participationPercent={Number(formValues.servicingRate)}
            bidBasisPoints={bidBasisPoints}
            servicingRate={Number(formValues.servicingRate)}
            defaultAssumptionType={formValues.defaultAssumptionType}
            cdr={formValues.cdr}
            prepaymentAssumptionType={formValues.prepaymentAssumptionType}
            cpr={formValues.cpr}
          />
        </div>
      </div>
    </div>
  );
};

export default YieldMatrixBidDetails;
